import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { logoutThunk, updateUserDetailSync } from "@/Store/Slices/authSlice";
import { getUserDetailsThunk } from "@/Store/Slices/UsersSliceNew";
import { getProfileImageUrl } from "../../Utils/utils";
// import NotificationDropdown from "./NotificationDropdown";
import ProfileCardIcon from "../../assets/icons/SvgIcons/ProfileCardIcon";
import CustomArrowIcon from "../../assets/icons/SvgIcons/CustomArrowIcon";
import ChevronDownIcon from "../../assets/icons/SvgIcons/ChevronDownIcon";
import AppBrandMark from "../Branding/AppBrandMark";

const Header = ({
  isSidebarCollapsed,
  isMobileTablet,
  setIsSidebarCollapsed,
}) => {
  return (
    <header
      className={`bg-white border-b-[0.5px] border-[#312F911A] h-[56px] px-[12px] lg:px-5 py-2 fixed top-0 right-0 left-0 flex items-center gap-2 transition-[left] duration-300 ease-in-out ${
        isSidebarCollapsed ? "lg:left-[72px]" : "lg:left-[240px]"
      } z-40`}
    >
      {isMobileTablet ? (
        <div className="flex items-center gap-2 min-w-0 mr-auto">
          <MobileToggle
            isSidebarCollapsed={isSidebarCollapsed}
            setIsSidebarCollapsed={setIsSidebarCollapsed}
          />
          <AppBrandMark variant="header" />
        </div>
      ) : null}

      <div className="h-full flex items-center justify-end gap-[20px] ml-auto shrink-0">
        {/* <NotificationDropdown /> */}

        <UserProfileDropdown />
      </div>
    </header>
  );
};

export default Header;

const UserProfileDropdown = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const userData = useSelector((state) => state?.authSlice?.user?.user);
  const profileImage = getProfileImageUrl(userData?.file);
  const dropdownRef = useRef(null);
  const isProfileRoute = location.pathname === "/profile";
  const handleLogout = async (e) => {
    e.preventDefault();
    closeDropdown();
    await dispatch(logoutThunk());
    navigate("/login", { replace: true });
  };

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    const syncProfile = async () => {
      if (!userData?.id || isProfileRoute) {
        return;
      }

      try {
        const result = await dispatch(
          getUserDetailsThunk(userData.id),
        ).unwrap();
        dispatch(
          updateUserDetailSync({
            file: result?.file || null,
            fileType: result?.fileType || null,
            name: result?.name,
          }),
        );
      } catch {
        // Keep existing auth profile data if refresh fails.
      }
    };

    syncProfile();
  }, [dispatch, isProfileRoute, userData?.id]);

  useEffect(() => {
    setImageError(false);
  }, [profileImage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative z-50" ref={dropdownRef}>
      <div className="flex items-center justify-center gap-[10px] cursor-pointer">
        <div
          className="flex items-center gap-[10px]"
          onClick={handleToggle}
          role="button"
          aria-expanded={isOpen}
        >
          {profileImage && !imageError ? (
            <img
              src={profileImage}
              alt="User Profile"
              className="rounded-full w-8 h-8 object-contain bg-white"
              onError={() => setImageError(true)}
            />
          ) : (
            <ProfileCardIcon
              className="rounded-full w-8 h-8"
              width="32"
              height="32"
              rx="100"
            />
          )}
          <div className="hidden min-[480px]:flex flex-col items-start text-left min-w-0 max-w-[140px]">
            <h5 className="text-black font-medium leading-tight text-[13px] md:text-[14px] select-none truncate w-full text-left">
              {userData?.name || "-"}
            </h5>
            <p className="text-text-grey text-[12px] font-[400] leading-[16px] select-none truncate w-full text-left">
              {userData?.role?.name || "-"}
            </p>
          </div>
          <ChevronDownIcon
            fill="#222222"
            className={`${isOpen ? "rotate-180" : ""}`}
          />
        </div>
        {/* <img src={NotificationRight} alt="Dropdown Arrow" /> */}
      </div>

      {isOpen && (
        <div className="absolute right-0 mt-[18px] w-[170px] sm:w-[198px] flex flex-col rounded-[16px] shadow-[0_0_30px_0_rgba(96,111,149,0.19)] overflow-hidden text-left">
          <div className="px-[16px] py-[12px] rounded-t-[16px] bg-main bg-[url('../src/assets/images/notification-bg.svg')] bg-no-repeat bg-contain">
            <h5 className="font-[600] text-[12px] text-white leading-[21px] select-none">
              User Profile
            </h5>
          </div>

          <ul className="bg-white py-[6px] flex flex-col">
            <li>
              <Link
                to="/profile"
                className="flex flex-col gap-1 px-[16px] py-[6px] mb-[2px] text-start bg-white"
                onClick={closeDropdown}
              >
                <h5 className="flex items-center gap-1 font-[500] text-[14px] text-main leading-[21px]">
                  {/* Icon omitted for brevity */}
                  My Profile
                </h5>
              </Link>
            </li>

            <li>
              <a
                href=""
                className="flex w-full flex-col gap-1 px-[16px] py-[6px] text-start bg-white"
                onClick={handleLogout}
              >
                <h5 className="flex items-center gap-1 font-[500] text-[14px] text-main leading-[21px]">
                  {/* Icon omitted for brevity */}
                  Logout
                </h5>
              </a>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const MobileToggle = ({ isSidebarCollapsed, setIsSidebarCollapsed }) => {
  const handleToggle = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  return (
    <button
      type="button"
      className="shrink-0 cursor-pointer"
      onClick={handleToggle}
      aria-label={isSidebarCollapsed ? "Open menu" : "Close menu"}
    >
      <CustomArrowIcon
        className={`w-6 h-6 transition-transform duration-300 ease-in-out ${
          isSidebarCollapsed ? "rotate-0" : "rotate-180"
        }`}
      />
    </button>
  );
};
